/**
 * Column Width Mixin
 * 
 * Provides reusable column width functionality for Vue components.
 * Integrates width calculation and validation utilities with Vue reactivity.
 * 
 * Constitutional Compliance: Presentation logic mixin - calculates styles for display only.
 */

import { calculateColumnWidths, calculateResponsiveWidths, enforceMinimumWidths, monitorPerformance } from '@/utils/tableWidth'
import { validateTableWidthConfiguration, validateEdgeCases } from '@/utils/widthValidator'

export const ColumnWidthMixin = {
  data() {
    return {
      // Container width tracking for responsive behavior
      containerWidth: null,
      // Width calculation cache to avoid redundant calculations
      widthCalculationCache: null,
      // Performance monitoring data
      performanceMetrics: null,
      // Validation warnings for developer feedback
      widthValidationWarnings: []
    }
  },

  computed: {
    /**
     * Validates all column width configurations in current schema
     * @returns {Object} Validation results for all columns
     */
    columnWidthValidation() {
      if (!this.schema || !this.schema.columns) {
        return { isValid: true, errors: [], warnings: [] }
      }

      return validateTableWidthConfiguration(this.schema.columns)
    },

    /**
     * Calculates final column widths based on schema and container size
     * @returns {Object} Width distribution results
     */
    calculatedWidths() {
      if (!this.schema || !this.schema.columns || !this.containerWidth) {
        return { widths: [], totalUsedWidth: 0, scalingApplied: false, warnings: [] }
      }

      const startTime = performance.now()
      
      // Use cached result if inputs haven't changed
      const cacheKey = this.generateWidthCacheKey()
      if (this.widthCalculationCache && this.widthCalculationCache.key === cacheKey) {
        return this.widthCalculationCache.result
      }

      const result = calculateColumnWidths(this.schema.columns, this.containerWidth)
      
      // Apply minimum width constraints
      result.widths = enforceMinimumWidths(result.widths, 50)
      
      const endTime = performance.now()
      const calculationTime = endTime - startTime

      // Monitor performance for large tables
      this.performanceMetrics = monitorPerformance(this.schema.columns.length, calculationTime)

      // Cache result for future use
      this.widthCalculationCache = {
        key: cacheKey,
        result: result
      }

      return result
    },

    /**
     * Generates CSS-ready column styles for table rendering
     * @returns {Array} Array of style objects for each column
     */
    columnStyles() {
      const widths = this.calculatedWidths.widths
      
      return this.schema.columns.map((column, index) => {
        const width = widths[index]
        const baseStyle = {
          boxSizing: 'border-box',
          overflow: 'hidden'
        }

        if (width) {
          baseStyle.width = width
          baseStyle.minWidth = width
          baseStyle.maxWidth = width
        }

        // Add text wrapping support for content preservation
        if (column.width && column.width !== '100%') {
          baseStyle.whiteSpace = 'normal'
          baseStyle.wordWrap = 'break-word'
          baseStyle.wordBreak = 'break-word'
        }

        return baseStyle
      })
    },

    /**
     * Generates table container CSS for width control
     * @returns {Object} CSS styles for table container
     */
    tableContainerStyle() {
      const hasWidthSpecs = this.schema.columns.some(col => col.width)
      
      const baseStyle = {
        width: '100%',
        overflow: 'auto'
      }

      if (hasWidthSpecs) {
        // Use fixed table layout when width specifications exist
        baseStyle.tableLayout = 'fixed'
      }

      return baseStyle
    },

    /**
     * Edge case validation for current width configuration
     * @returns {Object} Edge case analysis results
     */
    edgeCaseValidation() {
      if (!this.schema || !this.schema.columns || !this.containerWidth) {
        return { hasEdgeCases: false, scenarios: [] }
      }

      return validateEdgeCases(this.schema.columns, this.containerWidth)
    }
  },

  methods: {
    /**
     * Initializes container width tracking
     * Call this in mounted() hook to start width monitoring
     */
    initializeWidthTracking() {
      this.$nextTick(() => {
        this.updateContainerWidth()
        
        // Set up resize observer for responsive behavior
        if (this.$el && window.ResizeObserver) {
          this.resizeObserver = new ResizeObserver(this.handleContainerResize)
          this.resizeObserver.observe(this.$el)
        } else {
          // Fallback for older browsers
          window.addEventListener('resize', this.handleWindowResize)
        }
      })
    },

    /**
     * Updates container width measurement
     */
    updateContainerWidth() {
      if (this.$el) {
        // Use clientWidth for content width (excluding borders and scrollbars)
        // but fall back to getBoundingClientRect if clientWidth is not available
        let measuredWidth = this.$el.clientWidth || this.$el.getBoundingClientRect().width
        
        // Account for any Bootstrap table-responsive wrapper
        const tableResponsive = this.$el.closest('.table-responsive')
        if (tableResponsive) {
          measuredWidth = Math.min(measuredWidth, tableResponsive.clientWidth - 2) // -2px for potential borders
        }
        
        // Ensure we have a reasonable minimum and maximum
        this.containerWidth = Math.max(200, Math.min(measuredWidth, 2000))
      }
    },

    /**
     * Handles container resize events for responsive width behavior
     * @param {Array} entries - ResizeObserver entries
     */
    handleContainerResize(entries) {
      if (entries && entries.length > 0) {
        const newWidth = entries[0].contentRect.width
        
        // Only update if width change is significant (avoid excessive recalculation)
        if (Math.abs(newWidth - this.containerWidth) > 10) {
          const oldWidth = this.containerWidth
          this.containerWidth = newWidth
          this.clearWidthCache()
          
          // Emit resize event for custom handling if needed
          if (this.$emit) {
            this.$emit('container-width-changed', {
              oldWidth,
              newWidth,
              widthDelta: newWidth - oldWidth
            })
          }
          
          // Optimize fill column behavior for new width
          this.optimizeFillColumnBehavior(oldWidth, newWidth)
        }
      }
    },
    
    /**
     * Optimizes fill column behavior during responsive changes
     * @param {Number} oldWidth - Previous container width
     * @param {Number} newWidth - New container width
     */
    optimizeFillColumnBehavior(oldWidth, newWidth) {
      if (!this.schema || !this.schema.columns) return
      
      // Check if there are fill columns
      const hasFillColumn = this.schema.columns.some(col => col.width === '100%')
      
      if (hasFillColumn) {
        const widthChange = newWidth - oldWidth
        const changePercent = Math.abs(widthChange / oldWidth) * 100
        
        // For significant changes (>20%), provide feedback
        if (changePercent > 20 && this.$emit) {
          this.$emit('fill-column-optimized', {
            widthChange,
            changePercent,
            optimization: changePercent > 50 ? 'major-reflow' : 'minor-adjustment'
          })
        }
      }
    },

    /**
     * Handles window resize events (fallback for older browsers)
     */
    handleWindowResize() {
      // Debounce resize events to avoid excessive calculations
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        this.updateContainerWidth()
        this.clearWidthCache()
      }, 150)
    },

    /**
     * Generates cache key for width calculations
     * @returns {string} Unique key representing current width configuration
     */
    generateWidthCacheKey() {
      if (!this.schema || !this.schema.columns) {
        return 'no-schema'
      }

      const widthSpecs = this.schema.columns.map(col => col.width || 'auto').join('|')
      return `${widthSpecs}-${this.containerWidth}`
    },

    /**
     * Clears width calculation cache to force recalculation
     */
    clearWidthCache() {
      this.widthCalculationCache = null
    },

    /**
     * Gets column width specification for a specific column
     * @param {number} columnIndex - Index of the column
     * @returns {Object} Width specification details
     */
    getColumnWidthSpec(columnIndex) {
      if (!this.schema || !this.schema.columns || columnIndex >= this.schema.columns.length) {
        return { hasWidth: false, value: null, type: 'auto' }
      }

      const column = this.schema.columns[columnIndex]
      const width = column.width

      if (!width) {
        return { hasWidth: false, value: null, type: 'auto' }
      }

      let type = 'auto'
      if (width.endsWith('px')) {
        type = 'pixel'
      } else if (width === '100%') {
        type = 'fill'
      } else if (width.endsWith('%')) {
        type = 'percentage'
      }

      return {
        hasWidth: true,
        value: width,
        type: type,
        calculatedWidth: this.calculatedWidths.widths[columnIndex]
      }
    },

    /**
     * Updates width specification for a column (useful for dynamic editing)
     * @param {number} columnIndex - Index of the column to update
     * @param {string} newWidth - New width specification
     */
    updateColumnWidth(columnIndex, newWidth) {
      if (this.schema && this.schema.columns && columnIndex < this.schema.columns.length) {
        // Create a defensive copy to maintain Vue reactivity
        const updatedColumns = [...this.schema.columns]
        updatedColumns[columnIndex] = {
          ...updatedColumns[columnIndex],
          width: newWidth
        }
        
        // Emit change event for parent component to handle
        this.$emit('width-configuration-changed', {
          columnIndex,
          newWidth,
          updatedColumns
        })
        
        // Clear cache to force recalculation
        this.clearWidthCache()
      }
    },
    
    /**
     * Gets container width with caching and validation
     * @returns {Number} Current container width or fallback
     */
    getValidatedContainerWidth() {
      if (this.containerWidth && this.containerWidth > 0) {
        return this.containerWidth
      }
      
      // Fallback measurements if primary tracking failed
      if (this.$el) {
        const fallbackWidth = this.$el.clientWidth || this.$el.offsetWidth
        if (fallbackWidth > 0) {
          this.containerWidth = fallbackWidth
          return fallbackWidth
        }
      }
      
      // Last resort: reasonable default for fill column behavior
      return 800
    },
    
    /**
     * Tracks container width stability for performance optimization
     * @returns {Object} Width stability metrics
     */
    getContainerWidthMetrics() {
      const now = Date.now()
      
      // Initialize tracking if needed
      if (!this.widthStabilityTracking) {
        this.widthStabilityTracking = {
          measurements: [],
          lastStableWidth: this.containerWidth,
          stabilityDuration: 0
        }
      }
      
      const tracking = this.widthStabilityTracking
      tracking.measurements.push({
        width: this.containerWidth,
        timestamp: now
      })
      
      // Keep only last 10 measurements
      if (tracking.measurements.length > 10) {
        tracking.measurements = tracking.measurements.slice(-10)
      }
      
      // Calculate stability
      const recent = tracking.measurements.slice(-5)
      const isStable = recent.length >= 3 && 
        recent.every(m => Math.abs(m.width - this.containerWidth) < 5)
      
      if (isStable && tracking.lastStableWidth === this.containerWidth) {
        tracking.stabilityDuration = now - recent[0].timestamp
      } else {
        tracking.lastStableWidth = this.containerWidth
        tracking.stabilityDuration = 0
      }
      
      return {
        isStable,
        stabilityDuration: tracking.stabilityDuration,
        recentVariation: recent.length > 1 ? 
          Math.max(...recent.map(m => m.width)) - Math.min(...recent.map(m => m.width)) : 0
      }
    },
    
    /**
     * Forces immediate recalculation of column widths
     */
    recalculateWidths() {
      this.clearWidthCache()
      // Trigger computed property recalculation
      this.$forceUpdate()
    }
  },

  mounted() {
    // Initialize width tracking when component mounts
    this.initializeWidthTracking()
  },

  beforeDestroy() {
    // Clean up event listeners to prevent memory leaks
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
    }
    
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout)
    }
    
    window.removeEventListener('resize', this.handleWindowResize)
  },

  watch: {
    // Watch for schema changes to invalidate cache
    schema: {
      handler() {
        this.clearWidthCache()
      },
      deep: true
    },

    // Watch validation warnings to provide developer feedback
    'columnWidthValidation.warnings': {
      handler(newWarnings) {
        this.widthValidationWarnings = newWarnings
        
        // Log warnings in development mode
        if (process.env.NODE_ENV === 'development' && newWarnings.length > 0) {
          console.warn('Table width configuration warnings:', newWarnings)
        }
      },
      immediate: true
    },

    // Watch performance metrics for large table monitoring
    performanceMetrics: {
      handler(metrics) {
        if (metrics && metrics.warnings.length > 0) {
          // Log performance warnings in development
          if (process.env.NODE_ENV === 'development') {
            console.warn('Table width performance warnings:', metrics.warnings)
          }
        }
      }
    }
  }
}